import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { lspAPI, plaidAPI } from '../../services/api';
import { 
  Shield, Upload, CheckCircle, AlertCircle, Link as LinkIcon,
  FileText, Trash2, Calendar, DollarSign, Building2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlaidLink } from 'react-plaid-link';

const LSPVerification = () => {
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plaidConfigured, setPlaidConfigured] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);
  
  // Statement 1 form state
  const [statement1, setStatement1] = useState({
    date: '',
    balance: '',
    file: null
  });
  const [uploading1, setUploading1] = useState(false);
  
  // Statement 2 form state
  const [statement2, setStatement2] = useState({
    date: '',
    balance: '',
    file: null
  });
  const [uploading2, setUploading2] = useState(false);

  useEffect(() => {
    fetchVerification();
    checkPlaidStatus();
  }, []);

  const fetchVerification = async () => {
    try {
      const response = await lspAPI.getVerification();
      setVerification(response.data);
    } catch (error) {
      // Verification doesn't exist yet, create one
      try {
        const response = await lspAPI.createVerification();
        setVerification(response.data);
      } catch (err) {
        toast.error('Failed to initialize verification');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPlaidStatus = async () => {
    try {
      const response = await plaidAPI.getStatus();
      setPlaidConfigured(response.data.configured);
    } catch (error) {
      console.error('Failed to check Plaid status');
    }
  };

  const fetchLinkToken = async () => {
    if (!plaidConfigured) return;
    setPlaidLoading(true);
    try {
      const response = await plaidAPI.createLinkToken();
      setLinkToken(response.data.link_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize Plaid');
    } finally {
      setPlaidLoading(false);
    }
  };

  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setPlaidLoading(true);
    try {
      const response = await plaidAPI.exchangeToken(publicToken);
      toast.success(`Bank account connected! Verified liquidity: $${response.data.verified_liquidity?.toLocaleString()}`);
      fetchVerification();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect bank account');
    } finally {
      setPlaidLoading(false);
    }
  }, []);

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err) => {
      if (err) {
        console.error('Plaid error:', err);
      }
    }
  });

  const handlePlaidConnect = async () => {
    if (!linkToken) {
      await fetchLinkToken();
    }
    if (plaidReady) {
      openPlaid();
    } else {
      // Token was just fetched, will open automatically when ready
      toast.info('Initializing secure connection...');
    }
  };

  // Auto-open Plaid when link token is ready
  useEffect(() => {
    if (linkToken && plaidReady && plaidLoading) {
      openPlaid();
      setPlaidLoading(false);
    }
  }, [linkToken, plaidReady, plaidLoading, openPlaid]);

  const handleStatementUpload = async (statementNumber) => {
    const formData = statementNumber === 1 ? statement1 : statement2;
    const setUploading = statementNumber === 1 ? setUploading1 : setUploading2;
    const setFormData = statementNumber === 1 ? setStatement1 : setStatement2;
    
    if (!formData.file || !formData.date || !formData.balance) {
      toast.error('Please fill all fields');
      return;
    }

    setUploading(true);
    try {
      const response = await lspAPI.uploadBankStatement(
        formData.date, 
        parseFloat(formData.balance), 
        formData.file,
        statementNumber
      );
      toast.success(response.data.message);
      fetchVerification();
      setFormData({ date: '', balance: '', file: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteStatement = async (statementNumber) => {
    if (!window.confirm(`Are you sure you want to delete statement ${statementNumber}?`)) return;
    
    try {
      await lspAPI.deleteBankStatement(statementNumber);
      toast.success(`Statement ${statementNumber} deleted`);
      fetchVerification();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete statement');
    }
  };

  const refreshPlaidBalance = async () => {
    setPlaidLoading(true);
    try {
      const response = await plaidAPI.verifyBalance();
      toast.success(`Balance refreshed: $${response.data.verified_liquidity_amount?.toLocaleString()}`);
      fetchVerification();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh balance');
    } finally {
      setPlaidLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout title="LSP Verification">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const getStatusColor = (status) => {
    if (status === 'approved') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
  };

  const bankStatements = verification?.bank_statements || [];
  const statement1Data = bankStatements.find(s => s.statement_number === 1);
  const statement2Data = bankStatements.find(s => s.statement_number === 2);
  const bothStatementsUploaded = verification?.both_statements_uploaded || false;

  const StatementUploadCard = ({ number, existingData, formState, setFormState, uploading, onUpload, onDelete }) => (
    <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className={`w-8 h-8 ${existingData ? 'bg-green-100 dark:bg-green-900/30' : 'gradient-primary'} rounded-lg flex items-center justify-center`}>
            {existingData ? <CheckCircle className="w-4 h-4 text-green-600" /> : <FileText className="w-4 h-4 text-white" />}
          </div>
          Bank Statement #{number}
        </CardTitle>
        <CardDescription>
          {existingData ? 'Uploaded successfully' : 'Upload your bank statement from the last 60 days'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {existingData ? (
          <div className="space-y-3">
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Statement Date</p>
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {new Date(existingData.statement_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-600 dark:text-slate-400">Balance</p>
                  <p className="font-bold text-green-600 dark:text-green-400">
                    ${existingData.statement_balance?.toLocaleString()}
                  </p>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">{existingData.filename}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onDelete(number)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove & Re-upload
            </Button>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                Statement Date
              </Label>
              <Input
                type="date"
                value={formState.date}
                onChange={(e) => setFormState({ ...formState, date: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                data-testid={`statement-${number}-date`}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-slate-400" />
                Account Balance
              </Label>
              <Input
                type="number"
                placeholder="Enter balance amount"
                value={formState.balance}
                onChange={(e) => setFormState({ ...formState, balance: e.target.value })}
                data-testid={`statement-${number}-balance`}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-slate-400" />
                Bank Statement File
              </Label>
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 text-center hover:border-purple-400 transition-colors">
                <input
                  id={`file-${number}`}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setFormState({ ...formState, file: e.target.files[0] })}
                  className="hidden"
                  data-testid={`statement-${number}-file`}
                />
                <label htmlFor={`file-${number}`} className="cursor-pointer">
                  <FileText className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  {formState.file ? (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">{formState.file.name}</p>
                  ) : (
                    <p className="text-sm text-slate-600 dark:text-slate-400">Click to select PDF or image</p>
                  )}
                </label>
              </div>
            </div>

            <Button
              onClick={() => onUpload(number)}
              disabled={uploading || !formState.file || !formState.date || !formState.balance}
              className="w-full gradient-primary border-0 hover-scale"
              data-testid={`upload-statement-${number}-btn`}
            >
              {uploading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Statement #{number}
                </>
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Layout title="LSP Verification">
      <div className="space-y-6 animate-slide-in-up">
        {/* Status Overview */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-12 h-12 gradient-lsp rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              Verification Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Current Status</p>
                <span className={`px-4 py-2 rounded-lg font-semibold ${getStatusColor(verification?.verification_status)}`}>
                  {verification?.verification_status?.toUpperCase() || 'NOT STARTED'}
                </span>
              </div>
              {verification?.verification_status === 'approved' ? (
                <CheckCircle className="w-12 h-12 text-green-500" />
              ) : (
                <AlertCircle className="w-12 h-12 text-yellow-500" />
              )}
            </div>
            
            {/* Progress indicator */}
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">Verification Progress</span>
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {bothStatementsUploaded ? '2/2' : `${bankStatements.length}/2`} statements uploaded
                </span>
              </div>
              <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full gradient-primary transition-all duration-500"
                  style={{ width: `${(bankStatements.length / 2) * 100}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Statement Upload Cards */}
        <div className="grid lg:grid-cols-2 gap-6">
          <StatementUploadCard
            number={1}
            existingData={statement1Data}
            formState={statement1}
            setFormState={setStatement1}
            uploading={uploading1}
            onUpload={handleStatementUpload}
            onDelete={handleDeleteStatement}
          />
          <StatementUploadCard
            number={2}
            existingData={statement2Data}
            formState={statement2}
            setFormState={setStatement2}
            uploading={uploading2}
            onUpload={handleStatementUpload}
            onDelete={handleDeleteStatement}
          />
        </div>

        {/* Plaid Connection */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700 hover-lift">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-10 h-10 gradient-secondary rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              Instant Bank Verification (Plaid)
              {plaidConfigured && (
                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full ml-2">
                  Available
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Connect your bank account securely for instant balance verification
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {verification?.plaid_connected ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-semibold text-green-700 dark:text-green-400">Bank Connected via Plaid</p>
                        <p className="text-sm text-green-600 dark:text-green-500">
                          Verified Liquidity: ${verification.plaid_verified_liquidity_amount?.toLocaleString() || '0'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refreshPlaidBalance}
                      disabled={plaidLoading}
                      className="border-green-400 text-green-700 hover:bg-green-100 dark:hover:bg-green-900/30"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${plaidLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </div>
                {verification.plaid_accounts && verification.plaid_accounts.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Connected Accounts:</p>
                    {verification.plaid_accounts.map((acc, idx) => (
                      <div key={idx} className="flex justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {acc.name} ({acc.type}) •••{acc.mask}
                        </span>
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          ${acc.balance?.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : plaidConfigured ? (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Connect your bank account instantly using Plaid's secure connection. This is optional but provides real-time verification.
                </p>
                <Button
                  onClick={handlePlaidConnect}
                  disabled={plaidLoading}
                  className="w-full gradient-secondary border-0 hover-scale"
                  data-testid="connect-plaid-btn"
                >
                  {plaidLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Connect Bank Account with Plaid
                    </>
                  )}
                </Button>
              </>
            ) : (
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Plaid integration is being configured. Please use the bank statement upload option above for now.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="gradient-overlay border-purple-200 dark:border-purple-800 glass-effect">
          <CardHeader>
            <CardTitle>Verification Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <p className="text-slate-700 dark:text-slate-300">
                To access the deal marketplace, LSPs must complete the following:
              </p>
              <ol className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex items-start gap-2">
                  <span className={`font-semibold ${bankStatements.length >= 1 ? 'text-green-600' : 'text-purple-600'} dark:text-purple-400`}>1.</span>
                  <span>
                    <strong>Upload first bank statement</strong> (within last 60 days) showing account name and balance 
                    {statement1Data && <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`font-semibold ${bankStatements.length >= 2 ? 'text-green-600' : 'text-purple-600'} dark:text-purple-400`}>2.</span>
                  <span>
                    <strong>Upload second bank statement</strong> (different month or account)
                    {statement2Data && <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className={`font-semibold ${verification?.verification_status === 'approved' ? 'text-green-600' : 'text-purple-600'} dark:text-purple-400`}>3.</span>
                  <span>
                    <strong>Admin approval</strong> of your verification (typically within 24-48 hours)
                    {verification?.verification_status === 'approved' && <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-semibold text-slate-400">4.</span>
                  <span>
                    Plaid bank connection for instant verification
                    {verification?.plaid_connected && <CheckCircle className="inline w-4 h-4 ml-1 text-green-500" />}
                    <span className="ml-1 text-slate-500">(Optional)</span>
                  </span>
                </li>
              </ol>
              {bothStatementsUploaded && verification?.verification_status !== 'approved' && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">
                    ✓ <strong>Both statements uploaded!</strong> Your verification is pending admin review.
                  </p>
                </div>
              )}
              {verification?.verification_status === 'approved' && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-700 dark:text-green-400">
                    ✓ <strong>You're verified!</strong> You can now access the deal marketplace.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default LSPVerification;
